import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { trackEvent } from '@/components/Analytics';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        setAuthUser(data);
        if (data?.is_new_user) trackEvent('sign_up', { method: 'google' });
        // Round 86c (May 2026) — Preserve any ?query params that
        // came along on the redirect URL (e.g., ?stock=IONX&tab=my-portfolio
        // set by the Today-tab sign-in chip) so the post-login landing
        // can deep-link back to the same stock modal + tab.
        //
        // Round 104h (Jun 2026) — HARD navigation instead of SPA
        // navigate(). User report: after Google login the UI (gold
        // locks etc.) could render from pre-login state until a manual
        // hard refresh. A full document load here guarantees every
        // component re-fetches with the fresh session cookie — it's
        // the user's Ctrl+Shift+R, done automatically, once per login.
        const qs = location.search || '';
        // Round 116 (Feb 2026) — preserve pathname on post-login redirect.
        // Existing flows initiate OAuth with `origin + '/'` so pathname
        // is `/` here (behavior unchanged). New private routes (e.g.
        // `/med`) initiate OAuth with their own path and expect to
        // land back there after Google auth completes.
        const dest = (location.pathname && location.pathname !== '/')
          ? location.pathname
          : '/';
        window.location.replace(dest + qs);
      } catch (err) {
        console.error('Auth callback error:', err);
        navigate('/login', { replace: true });
      }
    })();
  }, [location.hash, location.search, navigate, setAuthUser]);

  return (
    <div className="min-h-screen min-h-[100svh] flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground text-sm">Signing you in...</p>
      </div>
    </div>
  );
}
